import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import BrandLogo from "@/components/BrandLogo";
import { useStoreSettings } from "@/hooks/useStoreSettings";
import {
  ShoppingCart, Heart, User, Search, ChevronRight, ChevronLeft,
  ChevronDown, Phone, Mail, Truck, Shield, CheckCircle, Headphones as HeadsetIcon,
  Menu, X, Star, Plus, Minus, ArrowRight, Tag, Package, HelpCircle, MessageCircle
} from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import {
  CategoryIconsRow,
  ShopByPriceSection,
  FlashDealsSection,
  PromoBannersNew,
  NewArrivalsSection,
  FeaturedCategoriesSection,
  WhyShopSection,
  TopDealsWeekSection,
  ResellerAppSection,
  CustomerReviewsSection,
  SecondarySliderSection,
} from "./home/HomeSections";
import { TileShowcaseSection, CategoryCardsSection, deviceClass } from "./home/CustomSections";
import { addToCart, quickAddToCart, getCartCount } from "@/utils/cart";
import { toast } from "sonner";
import { expandByVariant1 } from "@/utils/variantExpand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Retry the homepage config so a brief backend blip (restart / wake-up after a publish)
// never renders the built-in default sections in place of the store's own homepage.
async function getHomepageWithRetry(tries = 4) {
  for (let i = 0; i < tries; i++) {
    try { return await axios.get(`${API}/homepage`); }
    catch (e) {
      if (i === tries - 1) throw e;
      await new Promise(r => setTimeout(r, 1000 * (i + 1)));
    }
  }
}

// ── Helpers ────────────────────────────────────────────────────────────────
function fmt(n) {
  if (!n && n !== 0) return "";
  // Handle already-formatted strings like "29,999"
  const num = typeof n === "string" ? Number(n.replace(/,/g, "")) : Number(n);
  if (isNaN(num)) return String(n);
  return num.toLocaleString("en-IN");
}

function discount(mrp, price) {
  if (!mrp || !price || mrp <= price) return 0;
  return Math.round(((mrp - price) / mrp) * 100);
}

const TRUST_ICONS = {
  truck: Truck,
  shield: Shield,
  "badge-check": CheckCircle,
  headset: HeadsetIcon,
};

import VariantSwatches from "@/components/VariantSwatches";

function getWishlistIds() {
  try { return JSON.parse(localStorage.getItem("it_wishlist") || "[]"); } catch { return []; }
}
function toggleWishlistLocal(productId) {
  const list = getWishlistIds();
  const next = list.includes(productId) ? list.filter(id => id !== productId) : [...list, productId];
  localStorage.setItem("it_wishlist", JSON.stringify(next));
  window.dispatchEvent(new Event("wishlist-updated"));
  return !list.includes(productId);
}

// ── Product Card ───────────────────────────────────────────────────────────
function ProductCard({ product, onAddToCart }) {
  const disc = discount(product.mrp, product.deal_price_incl_gst);
  const [wishlisted, setWishlisted] = useState(() => getWishlistIds().includes(product.id));

  const handleWishlist = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const added = toggleWishlistLocal(product.id);
    setWishlisted(added);
    toast.success(added ? "Added to wishlist" : "Removed from wishlist");
  };

  return (
    <div
      data-testid={`product-card-${product.id}`}
      className="group bg-white rounded-xl border border-slate-100 overflow-hidden hover:shadow-md transition-all duration-200 flex flex-col"
    >
      <Link to={`/products/${product.url_slug || product.id}`} className="block relative overflow-hidden bg-slate-50" style={{ paddingBottom: "100%" }}>
        <img
          src={product.main_image || "https://images.unsplash.com/photo-1586953208270-d7db5e9ac21e?w=300"}
          alt={product.item_name}
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
          decoding="async"
        />
        {disc > 0 && (
          <span className="absolute top-2 left-2 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
            -{disc}%
          </span>
        )}
        {/* Wishlist heart */}
        <button
          onClick={handleWishlist}
          data-testid={`wishlist-btn-${product.id}`}
          className={`absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center shadow-sm transition-all opacity-0 group-hover:opacity-100 ${
            wishlisted ? "bg-red-50 opacity-100" : "bg-white"
          }`}
        >
          <Heart className={`w-3.5 h-3.5 ${wishlisted ? "fill-red-500 text-red-500" : "text-slate-400"}`} />
        </button>
      </Link>
      <div className="p-3 flex flex-col gap-1.5 flex-1">
        {product.brand_name && (
          <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wide">{product.brand_name}</p>
        )}
        <Link to={`/products/${product.url_slug || product.id}`} className="text-xs font-semibold text-slate-800 leading-snug line-clamp-2 hover:text-orange-600 transition-colors">
          {product.item_name}
        </Link>
        <VariantSwatches product={product} max={4} />
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className="text-sm font-bold text-slate-900">₹{fmt(product.deal_price_incl_gst)}</span>
          {product.mrp > product.deal_price_incl_gst && (
            <span className="text-[11px] text-slate-400 line-through">₹{fmt(product.mrp)}</span>
          )}
        </div>
        {product.star_rating > 0 && (
          <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map(i => (
              <Star key={i} className={`w-2.5 h-2.5 ${i <= Math.round(product.star_rating) ? "fill-yellow-400 text-yellow-400" : "text-slate-200"}`} />
            ))}
            {product.num_reviews > 0 && <span className="text-[10px] text-slate-400 ml-0.5">({product.num_reviews})</span>}
          </div>
        )}
        <button
          data-testid={`add-to-cart-${product.id}`}
          onClick={() => onAddToCart(product)}
          className="mt-auto w-full py-1.5 text-xs font-bold bg-orange-50 hover:bg-orange-500 text-orange-600 hover:text-white border border-orange-200 hover:border-orange-500 rounded-lg transition-all duration-200"
        >
          Quick Add
        </button>
      </div>
    </div>
  );
}

// ── Top Bar ────────────────────────────────────────────────────────────────
function TopBar({ config }) {
  if (!config?.enabled) return null;
  return (
    <div className="text-xs py-2 px-4" style={{ background: config.bg_color || "#0f172a" }}>
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="hidden md:flex items-center gap-4 text-slate-400">
          {config.phone && (
            <span className="flex items-center gap-1">
              <Phone className="w-3 h-3" /> {config.phone}
            </span>
          )}
          {config.email && (
            <span className="flex items-center gap-1">
              <Mail className="w-3 h-3" /> {config.email}
            </span>
          )}
        </div>
        {config.notice && (
          <p className="text-slate-300 text-center flex-1 truncate">{config.notice}</p>
        )}
        <div className="hidden md:flex items-center gap-3 text-slate-400 flex-shrink-0">
          <Link to="/account" className="hover:text-white transition-colors">Track Order</Link>
          {config.email && (
            <>
              <span className="text-slate-700">|</span>
              <a href={`mailto:${config.email}`} className="hover:text-white transition-colors">Need Help?</a>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Header ────────────────────────────────────────────────────────────
function MainHeader({ cartCount, wishlistCount = 0 }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const customerUser = (() => {
    try { return JSON.parse(localStorage.getItem("it_customer_user") || "null"); } catch { return null; }
  })();

  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim()) navigate(`/search?q=${encodeURIComponent(search.trim())}`);
  };

  return (
    <header className="bg-white border-b border-slate-100 sticky top-0 z-40 shadow-sm">
      <div className="max-w-[1800px] mx-auto px-3 py-3 flex items-center gap-4">
        {/* Logo */}
        <Link to="/" className="flex-shrink-0">
          <BrandLogo size="md" />
          <p className="text-[9px] text-slate-400 -mt-0.5 font-medium">Products for a Better Everyday</p>
        </Link>

        {/* Search */}
        <form onSubmit={handleSearch} className="flex-1 max-w-2xl hidden sm:flex">
          <div className="flex w-full border-2 border-orange-500 rounded-lg overflow-hidden">
            <input
              data-testid="header-search-input"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search for products, brands and more…"
              className="flex-1 px-4 py-2.5 text-sm focus:outline-none text-slate-700"
            />
            <button type="submit" className="bg-orange-500 hover:bg-orange-600 px-4 transition-colors">
              <Search className="w-4 h-4 text-white" />
            </button>
          </div>
        </form>

        {/* Icons */}
        <div className="flex items-center gap-3 ml-auto flex-shrink-0">
          <Link to="/wishlist" data-testid="header-wishlist-btn" className="relative text-slate-600 hover:text-orange-500 transition-colors hidden sm:block">
            <Heart className={`w-5 h-5 ${wishlistCount > 0 ? "fill-orange-500 text-orange-500" : ""}`} />
            {wishlistCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-orange-500 text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                {wishlistCount}
              </span>
            )}
          </Link>
          <Link to="/cart" data-testid="header-cart-btn" className="relative text-slate-600 hover:text-orange-500 transition-colors">
            <ShoppingCart className="w-5 h-5" />
            {cartCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-orange-500 text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                {cartCount}
              </span>
            )}
          </Link>
          {customerUser ? (
            <Link to="/account" data-testid="header-account-btn"
              className="hidden sm:flex items-center gap-1.5 text-xs text-slate-600 hover:text-orange-500 transition-colors border border-slate-200 hover:border-orange-400 rounded-lg px-2.5 py-1.5">
              <User className="w-3.5 h-3.5" />
              <span className="font-medium">{customerUser.name?.split(" ")[0] || "Account"}</span>
            </Link>
          ) : (
            <Link to="/customer-login" data-testid="header-login-btn"
              className="hidden sm:flex items-center gap-1.5 text-xs text-slate-600 hover:text-orange-500 transition-colors border border-slate-200 hover:border-orange-400 rounded-lg px-2.5 py-1.5">
              <User className="w-3.5 h-3.5" />
              <span className="font-medium">Sign In</span>
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}

// ── Nav Bar ────────────────────────────────────────────────────────────────
function NavBar({ menu, collections }) {
  const [catOpen, setCatOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const catRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (catRef.current && !catRef.current.contains(e.target)) setCatOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <nav className="bg-slate-900 text-white relative z-30">
      <div className="max-w-[1800px] mx-auto px-3">
        <div className="flex items-center gap-0">
          {/* Category Dropdown */}
          <div ref={catRef} className="relative flex-shrink-0">
            <button
              data-testid="nav-category-btn"
              onClick={() => setCatOpen(v => !v)}
              className="flex items-center gap-2 px-4 py-3 text-sm font-semibold bg-orange-500 hover:bg-orange-600 transition-colors"
            >
              <Menu className="w-4 h-4" />
              Category
              <ChevronDown className={`w-3 h-3 transition-transform ${catOpen ? "rotate-180" : ""}`} />
            </button>

            {catOpen && collections.length > 0 && (
              <div className="absolute top-full left-0 bg-white text-slate-800 shadow-2xl rounded-b-xl min-w-[220px] py-2 z-50 border border-slate-100">
                {collections.map((col, i) => (
                  <Link
                    key={i}
                    to={`/c/${col.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}
                    onClick={() => setCatOpen(false)}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-orange-50 hover:text-orange-600 transition-colors"
                  >
                    <Package className="w-3.5 h-3.5 text-slate-400" />
                    {col}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Menu Items (desktop) */}
          <div className="hidden md:flex items-center flex-1 overflow-x-auto scrollbar-hide">
            {menu.map((item) => (
              <Link
                key={item.id}
                to={item.url || "/"}
                className="flex items-center gap-1.5 px-4 py-3 text-sm font-medium text-slate-300 hover:text-white hover:bg-slate-800 transition-colors flex-shrink-0"
              >
                {item.label}
                {item.badge && (
                  <span
                    className="text-[9px] font-bold px-1.5 py-0.5 rounded text-white"
                    style={{ background: item.badge_color || "#f97316" }}
                  >
                    {item.badge}
                  </span>
                )}
              </Link>
            ))}
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden ml-auto px-4 py-3 text-slate-400 hover:text-white"
            onClick={() => setMobileOpen(v => !v)}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden py-2 border-t border-slate-800">
            {menu.map((item) => (
              <Link
                key={item.id}
                to={item.url || "/"}
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-between px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-slate-800"
              >
                {item.label}
                {item.badge && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded text-white"
                    style={{ background: item.badge_color || "#f97316" }}>
                    {item.badge}
                  </span>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
}

// ── Hero Section ───────────────────────────────────────────────────────────
function HeroSection({ config }) {
  const [cur, setCur] = useState(0);
  const [paused, setPaused] = useState(false);
  // Responsive image: prefer mobile image on small screens
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 768);
  const slides = config?.slides || [];
  const interval = config?.autoplay_interval || 5000;

  useEffect(() => {
    if (slides.length <= 1 || paused) return;
    const t = setInterval(() => setCur(c => (c + 1) % slides.length), interval);
    return () => clearInterval(t);
  }, [slides.length, interval, paused]);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  if (!config?.enabled) return null;

  if (slides.length === 0) {
    return (
      <div className="bg-gradient-to-r from-slate-900 to-slate-700 text-white flex items-center justify-center w-full" style={{ aspectRatio: "1600 / 500" }}>
        <div className="text-center p-8">
          <div className="mb-2 flex justify-center"><BrandLogo size="xl" textVariant="light" /></div>
          <p className="text-slate-400">Quality Products for a Better Everyday</p>
          <Link to="/admin/homepage" className="inline-block mt-4 bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-2.5 rounded-lg text-sm transition-colors">
            Configure Homepage →
          </Link>
        </div>
      </div>
    );
  }

  const slide = slides[cur];
  // Pick the right image based on screen width
  const heroImage = isMobile
    ? (slide.image_url_mobile || slide.image_url)
    : slide.image_url;
  const bgStyle = heroImage
    ? { backgroundImage: `url(${heroImage})`, backgroundSize: "cover", backgroundPosition: "center" }
    : { background: slide.bg_color || "linear-gradient(135deg,#1e293b,#334155)" };

  return (
    <div
      className="relative overflow-hidden select-none w-full"
      style={{ aspectRatio: "1600 / 500", ...bgStyle }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="hero-section"
    >
      {/* Overlay */}
      {heroImage && <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent" />}

      <div className="relative max-w-7xl mx-auto px-8 h-full flex items-center">
        <div className="max-w-lg text-white py-10">
          {slide.badge && (
            <span className="inline-block bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full mb-4">
              {slide.badge}
            </span>
          )}
          {slide.title && (
            <h1 className="text-4xl md:text-5xl font-black leading-tight mb-3">{slide.title}</h1>
          )}
          {slide.subtitle && (
            <p className="text-slate-300 text-base mb-4 leading-relaxed">{slide.subtitle}</p>
          )}
          {(slide.original_price || slide.sale_price) && (
            <div className="flex items-baseline gap-3 mb-6">
              {slide.original_price && (
                <span className="text-slate-400 line-through text-lg">Was ₹{fmt(slide.original_price)}</span>
              )}
              {slide.sale_price && (
                <span className="text-orange-400 text-3xl font-black">Now ₹{fmt(slide.sale_price)}</span>
              )}
            </div>
          )}
          {slide.cta_text && slide.cta_url && (
            <Link to={slide.cta_url}
              className="inline-block bg-orange-500 hover:bg-orange-600 text-white font-bold px-7 py-3 rounded-lg text-sm transition-colors shadow-lg">
              {slide.cta_text}
            </Link>
          )}
        </div>
      </div>

      {/* Nav arrows */}
      {slides.length > 1 && (
        <>
          <button onClick={() => setCur(c => (c - 1 + slides.length) % slides.length)}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/20 hover:bg-white/40 backdrop-blur-sm text-white rounded-full flex items-center justify-center transition-all">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button onClick={() => setCur(c => (c + 1) % slides.length)}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/20 hover:bg-white/40 backdrop-blur-sm text-white rounded-full flex items-center justify-center transition-all">
            <ChevronRight className="w-4 h-4" />
          </button>
        </>
      )}

      {/* Dots */}
      {slides.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
          {slides.map((_, i) => (
            <button key={i} onClick={() => setCur(i)}
              className={`rounded-full transition-all ${i === cur ? "w-6 h-2 bg-orange-500" : "w-2 h-2 bg-white/50"}`} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Category Grid ──────────────────────────────────────────────────────────
// ── Category Grid Section (Multiple product grids, Recommended-style layout) ─
function CategoryGridProductSection({ section, allProducts }) {
  // Auto-migrate: if admin set title but not collection (old format), use title as collection
  const collection = section.collection || section.title || "";
  const limit = section.limit || 10;
  const categorySlug = collection
    ? collection.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")
    : "";

  const products = useMemo(() => {
    let filtered = collection
      ? allProducts.filter(p => {
          const cols = Array.isArray(p.collection) ? p.collection : [p.collection];
          return cols.some(c => c?.toLowerCase() === collection.toLowerCase());
        })
      : allProducts;
    filtered = expandByVariant1(filtered);
    return filtered.slice(0, limit);
  }, [allProducts, collection, limit]);

  if (!products.length) return null;

  const handleAdd = (prod) => {
    quickAddToCart(prod);
    toast.success("Added to cart");
    window.dispatchEvent(new Event("it_cart_updated"));
  };

  return (
    <section className="max-w-[1800px] mx-auto px-3 py-8" data-testid={`cat-grid-section-${section.id || section.title}`}>
      <div className="flex items-center justify-between mb-5 border-b-2 border-slate-100 pb-3">
        <div className="flex items-center gap-2">
          <div className="w-1 h-5 bg-orange-500 rounded-full" />
          <h2 className="text-base font-black text-slate-900 uppercase tracking-wide">
            {section.title || "Products"}
          </h2>
        </div>
        {categorySlug ? (
          <Link to={`/c/${categorySlug}`}
            className="text-xs text-blue-600 hover:underline font-medium flex items-center gap-1 transition-colors">
            View More <ArrowRight className="w-3 h-3" />
          </Link>
        ) : (
          <button className="text-xs text-blue-600 hover:underline font-medium flex items-center gap-1">
            View More <ArrowRight className="w-3 h-3" />
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {products.map(p => <ProductCard key={p.id} product={p} onAddToCart={handleAdd} />)}
      </div>
    </section>
  );
}

function CategoryGridSection({ config, allProducts }) {
  if (!config?.enabled) return null;
  const sections = (config.items || []).filter(s => s.enabled !== false);
  if (!sections.length) return null;
  return (
    <>
      {sections.map((section, i) => (
        <CategoryGridProductSection key={section.id || i} section={section} allProducts={allProducts} />
      ))}
    </>
  );
}

// ── Brands Section ─────────────────────────────────────────────────────────
function BrandsSection({ config }) {
  if (!config?.enabled || !config.items?.length) return null;

  return (
    <section className="bg-white border-y border-slate-100 py-8" data-testid="brands-section">
      <div className="max-w-[1800px] mx-auto px-3">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-base font-black text-slate-900 uppercase tracking-wider">
            {config.title || "Brand We Love"}
          </h2>
          {config.view_all_url && (
            <Link to={config.view_all_url} className="text-xs text-blue-600 hover:underline font-medium flex items-center gap-1">
              View All Brands <ArrowRight className="w-3 h-3" />
            </Link>
          )}
        </div>
        <div className="flex items-center gap-6 overflow-x-auto scrollbar-hide pb-2">
          {config.items.map((brand, i) => (
            <Link key={brand.id || i} to={brand.url || "/"}
              className="flex-shrink-0 flex flex-col items-center gap-2 group">
              {brand.logo_url ? (
                <img src={brand.logo_url} alt={brand.name}
                  className="h-10 w-auto object-contain grayscale group-hover:grayscale-0 transition-all opacity-60 group-hover:opacity-100"
                  loading="lazy" decoding="async" />
              ) : (
                <span className="font-black text-lg text-slate-400 group-hover:text-slate-700 transition-colors tracking-tighter">
                  {brand.name}
                </span>
              )}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Promo Banners ──────────────────────────────────────────────────────────
function PromoBannersSection({ config }) {
  if (!config?.enabled || !config.items?.length) return null;

  return (
    <section className="max-w-[1800px] mx-auto px-3 py-8" data-testid="promo-banners-section">
      <div className={`grid gap-4 ${config.items.length === 1 ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"}`}>
        {config.items.map((banner, i) => (
          <Link key={banner.id || i} to={banner.url || "/"}
            className="relative overflow-hidden rounded-xl group flex items-center"
            style={{ background: banner.bg_color || "#f1f5f9", minHeight: 160 }}>
            {banner.image_url && (
              <img src={banner.image_url} alt={banner.title}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                loading="lazy" decoding="async" />
            )}
            <div className={`absolute inset-0 ${banner.image_url ? "bg-gradient-to-r from-black/60 to-transparent" : ""}`} />
            <div className="relative p-6 text-white">
              {banner.title && <p className="text-xl font-black leading-tight">{banner.title}</p>}
              {banner.subtitle && <p className="text-sm text-slate-200 mt-1">{banner.subtitle}</p>}
              {banner.cta_text && (
                <span className="inline-flex items-center gap-1 text-xs font-bold text-orange-300 mt-3 group-hover:gap-2 transition-all">
                  {banner.cta_text} <ArrowRight className="w-3 h-3" />
                </span>
              )}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

// ── Featured Product Card (left large card in category section) ─────────────
function FeaturedProductCard({ product }) {
  const price = product.deal_price_incl_gst || product.mrp || 0;
  const mrp = product.mrp || 0;
  const discount = mrp > price ? Math.round((1 - price / mrp) * 100) : 0;
  return (
    <Link to={`/products/${product.url_slug}`}
      className="relative overflow-hidden rounded-2xl block w-full h-full group cursor-pointer"
      style={{ minHeight: 420 }}>
      <div className="absolute inset-0 bg-slate-800">
        {product.main_image && (
          <img src={product.main_image} alt={product.item_name}
            className="w-full h-full object-cover opacity-40 group-hover:opacity-55 group-hover:scale-105 transition-all duration-500"
            loading="lazy" decoding="async" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-slate-700/10" />
      </div>
      <div className="relative flex flex-col justify-end p-5 text-white" style={{ minHeight: 420 }}>
        {discount > 0 && (
          <span className="inline-block bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded mb-2 self-start">-{discount}%</span>
        )}
        <p className="text-xs text-slate-300 uppercase tracking-wide font-medium">{product.brand_name}</p>
        <p className="text-sm font-bold mt-1 leading-snug line-clamp-2">{product.item_name}</p>
        <div className="flex items-baseline gap-2 mt-2">
          <span className="text-lg font-black">₹{fmt(price)}</span>
          {mrp > price && <span className="text-xs text-slate-400 line-through">₹{fmt(mrp)}</span>}
        </div>
        <span className="inline-flex items-center gap-1 mt-3 text-xs font-semibold text-orange-300 group-hover:gap-2 transition-all">
          View Product <ArrowRight className="w-3 h-3" />
        </span>
      </div>
    </Link>
  );
}

// ── Category Product Section ───────────────────────────────────────────────
function CategoryProductSection({ section, allProducts }) {
  const [activeFilter, setActiveFilter] = useState("All");

  const sectionProducts = useMemo(() => {
    let filtered = allProducts.filter(p => {
      if (!section.collection) return true;
      const cols = Array.isArray(p.collection) ? p.collection : [p.collection];
      return cols.some(c => c?.toLowerCase() === section.collection?.toLowerCase());
    });
    // Expand by Variant 1 so each color/variant gets its own card
    filtered = expandByVariant1(filtered);
    if (activeFilter === "New") {
      filtered = [...filtered].reverse();
    } else if (activeFilter === "Best Sellers") {
      filtered = [...filtered].sort((a, b) => (b.num_reviews || 0) - (a.num_reviews || 0));
    } else if (activeFilter === "Featured") {
      filtered = [...filtered].sort((a, b) => (b.star_rating || 0) - (a.star_rating || 0));
    }
    return filtered;
  }, [allProducts, section.collection, activeFilter]);

  const categorySlug = section.collection
    ? section.collection.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")
    : "";

  if (!section.enabled && section.enabled !== undefined) return null;

  const featuredProduct = sectionProducts[0];
  const gridProducts = sectionProducts.slice(1, 5);

  return (
    <section className="max-w-[1800px] mx-auto px-3 py-6" data-testid={`cat-section-${section.id}`}>
      {/* Section header */}
      <div className="flex items-center justify-between mb-4 border-b-2 border-slate-100 pb-3">
        <div className="flex items-center gap-2">
          <div className="w-1 h-5 bg-orange-500 rounded-full" />
          <h2 className="text-base font-black text-slate-900 uppercase tracking-wide">{section.title}</h2>
        </div>
        <div className="flex items-center gap-1 text-xs">
          {["All", "New", "Best Sellers", "Featured"].map(f => (
            <button key={f} onClick={() => setActiveFilter(f)}
              className={`px-3 py-1 rounded-full font-semibold transition-all ${activeFilter === f ? "bg-orange-500 text-white" : "text-slate-500 hover:text-orange-500"}`}>
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* 5-product layout */}
      {sectionProducts.length === 0 ? (
        <div className="flex items-center justify-center h-40 bg-slate-50 rounded-xl">
          <p className="text-sm text-slate-400">No products in this collection yet</p>
        </div>
      ) : (
        <div className="flex gap-4 items-stretch">
          {/* Featured card — left large portrait slot */}
          {featuredProduct && (
            <div className="hidden md:flex w-56 lg:w-64 flex-shrink-0">
              <FeaturedProductCard product={featuredProduct} />
            </div>
          )}
          {/* 4 regular product cards */}
          <div className="flex-1 grid grid-cols-2 lg:grid-cols-4 gap-3">
            {gridProducts.map(p => (
              <ProductCard key={p.id} product={p} onAddToCart={(prod) => {
                quickAddToCart(prod);
                toast.success("Added to cart");
                window.dispatchEvent(new Event("it_cart_updated"));
              }} />
            ))}
          </div>
        </div>
      )}

      {/* View All */}
      {categorySlug && (
        <div className="flex justify-center mt-5">
          <Link to={`/c/${categorySlug}`} data-testid={`view-all-${section.id}`}
            className="inline-flex items-center gap-2 border border-slate-300 hover:border-orange-400 hover:text-orange-500 text-sm font-semibold px-8 py-2.5 rounded-full transition-all group">
            View All <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>
      )}
    </section>
  );
}

// ── Recommended Section ────────────────────────────────────────────────────
function RecommendedSection({ config, allProducts }) {
  if (!config?.enabled) return null;
  const limit = config.limit || 10;
  const products = config.collection
    ? allProducts.filter(p => {
        const cols = Array.isArray(p.collection) ? p.collection : [p.collection];
        return cols.some(c => c?.toLowerCase() === config.collection?.toLowerCase());
      }).slice(0, limit)
    : allProducts.slice(0, limit);

  if (!products.length) return null;

  const handleAdd = (prod) => {
    quickAddToCart(prod);
    toast.success("Added to cart");
    window.dispatchEvent(new Event("it_cart_updated"));
  };

  return (
    <section className="max-w-[1800px] mx-auto px-3 py-8" data-testid="recommended-section">
      <div className="flex items-center justify-between mb-5 border-b-2 border-slate-100 pb-3">
        <div className="flex items-center gap-2">
          <div className="w-1 h-5 bg-orange-500 rounded-full" />
          <h2 className="text-base font-black text-slate-900 uppercase tracking-wide">
            {config.title || "Recommended For You"}
          </h2>
        </div>
        <button className="text-xs text-blue-600 hover:underline font-medium flex items-center gap-1">
          View More <ArrowRight className="w-3 h-3" />
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {products.map(p => <ProductCard key={p.id} product={p} onAddToCart={handleAdd} />)}
      </div>
    </section>
  );
}

// ── Trust Badges ───────────────────────────────────────────────────────────
function TrustBadgesSection({ config }) {
  if (!config?.enabled || !config.items?.length) return null;

  return (
    <section className="bg-slate-50 border-y border-slate-100 py-8" data-testid="trust-badges-section">
      <div className="max-w-[1800px] mx-auto px-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {config.items.map((badge, i) => {
            const Icon = TRUST_ICONS[badge.icon] || Truck;
            return (
              <div key={badge.id || i} className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-800">{badge.title}</p>
                  {badge.subtitle && <p className="text-xs text-slate-500 mt-0.5">{badge.subtitle}</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ── Newsletter Section ─────────────────────────────────────────────────────
function NewsletterSection({ config }) {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  if (!config?.enabled) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email.includes("@")) { toast.error("Enter a valid email"); return; }
    setSubmitted(true);
    toast.success("You've subscribed! Thank you.");
  };

  return (
    <section className="bg-slate-900 py-12 text-white" data-testid="newsletter-section">
      <div className="max-w-xl mx-auto px-4 text-center">
        <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center mx-auto mb-4">
          <Tag className="w-5 h-5" />
        </div>
        <h2 className="text-xl font-black mb-2">{config.title || "Sign Up For Our Newsletter"}</h2>
        <p className="text-slate-400 text-sm mb-6">{config.subtitle}</p>
        {submitted ? (
          <p className="text-green-400 font-semibold">You're subscribed! ✓</p>
        ) : (
          <form onSubmit={handleSubmit} className="flex gap-2">
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Enter your email address"
              className="flex-1 px-4 py-3 rounded-lg text-slate-800 text-sm focus:outline-none"
              data-testid="newsletter-email-input"
            />
            <button type="submit"
              data-testid="newsletter-submit-btn"
              className="bg-orange-500 hover:bg-orange-600 px-6 py-3 rounded-lg text-sm font-bold transition-colors flex-shrink-0">
              Subscribe
            </button>
          </form>
        )}
      </div>
    </section>
  );
}

// ── Categories Showcase Section (arched card grid) ─────────────────────────
function CategoriesShowcaseSection({ config }) {
  if (!config?.enabled) return null;
  const items = config.items || [];
  if (!items.length) return null;

  return (
    <section className="py-10 px-4" data-testid="categories-showcase-section">
      <div className="max-w-7xl mx-auto">
        {config.title && (
          <h2 className="text-2xl font-black text-slate-900 mb-6">{config.title}</h2>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {items.map((item) => (
            <Link
              key={item.id}
              to={`/c/${item.slug || item.name?.toLowerCase().replace(/\s+/g, "-")}`}
              className="group block"
              data-testid={`cat-showcase-item-${item.id}`}
            >
              {/* Arch image area */}
              <div
                className="overflow-hidden relative bg-slate-100"
                style={{
                  borderRadius: "50% 50% 0 0 / 28% 28% 0 0",
                  aspectRatio: "3 / 3.6",
                  background: item.bg_color || "#f1f5f9",
                }}
              >
                {item.image ? (
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package className="w-12 h-12 text-slate-300" />
                  </div>
                )}
                {/* Gradient overlay with tagline */}
                {item.tagline && (
                  <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/10 flex flex-col justify-start p-4 pt-5">
                    {item.tagline.split(" ").map((word, i, arr) => (
                      <span
                        key={i}
                        className={`block font-black leading-tight ${
                          i === arr.length - 1
                            ? "text-yellow-300 italic text-sm"
                            : "text-white text-sm"
                        }`}
                      >
                        {word}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              {/* Bottom bar */}
              <div className="bg-slate-800 flex items-center gap-2 px-3 py-2.5 rounded-b-xl group-hover:bg-slate-900 transition-colors">
                {item.icon ? (
                  <span className="text-base flex-shrink-0">{item.icon}</span>
                ) : (
                  <div className="w-5 h-5 rounded-full bg-orange-500 flex-shrink-0" />
                )}
                <span className="flex-1 text-white text-xs font-semibold truncate">{item.name}</span>
                <div className="w-6 h-6 rounded-full border border-slate-500 flex items-center justify-center flex-shrink-0 group-hover:border-orange-400 transition-colors">
                  <ChevronRight className="w-3 h-3 text-slate-300 group-hover:text-orange-400 transition-colors" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── B2B / Wholesale Section ────────────────────────────────────────────────
function B2BSection({ config }) {
  const [openFaq, setOpenFaq] = useState(null);
  if (!config?.enabled) return null;

  const buyerTypes = config.buyer_types || [];
  const usps = config.usps || [];
  const faqItems = config.faq_items || [];

  return (
    <section data-testid="b2b-section">
      {/* ── Part 1: Header + Buyer Types ── */}
      <div className="py-12 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-5 mb-8">
            <div>
              {config.label && (
                <p className="text-[11px] font-bold tracking-widest text-slate-400 uppercase mb-3">
                  {config.label}
                </p>
              )}
              <h2 className="text-3xl md:text-4xl font-black text-slate-900 leading-tight max-w-xl">
                {config.title || "Wholesale for every kind of buyer."}
              </h2>
            </div>
            {config.cta_text && (
              <a
                href={config.cta_url || "/register"}
                className="self-start bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-full text-sm font-bold flex items-center gap-2 transition-colors flex-shrink-0"
              >
                {config.cta_text}
                <ArrowRight className="w-4 h-4" />
              </a>
            )}
          </div>

          {buyerTypes.length > 0 && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {buyerTypes.map((bt) => (
                <div key={bt.id} className="border border-slate-200 rounded-2xl p-5 hover:shadow-md transition-shadow">
                  {bt.icon && <div className="text-2xl mb-3">{bt.icon}</div>}
                  <p className="font-bold text-slate-900 text-sm mb-1.5">{bt.title}</p>
                  <p className="text-xs text-slate-500 leading-relaxed">{bt.description}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Part 2: USP Dark Bar ── */}
      {usps.length > 0 && (
        <div className="bg-slate-900 py-8 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-700">
              {usps.map((usp) => (
                <div key={usp.id} className="flex items-start gap-4 py-5 md:py-0 md:px-8 first:md:pl-0 last:md:pr-0">
                  {usp.icon && <span className="text-2xl flex-shrink-0">{usp.icon}</span>}
                  <div>
                    <p className="text-white font-bold text-base">{usp.title}</p>
                    {usp.description && (
                      <p className="text-slate-400 text-sm mt-0.5">{usp.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {config.usps_footer && (
              <p className="text-slate-500 text-xs border-t border-slate-800 pt-5 mt-5">
                {config.usps_footer}
              </p>
            )}
          </div>
        </div>
      )}

      {/* ── Part 3: FAQ ── */}
      {faqItems.length > 0 && (
        <div className="py-14 px-4 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              {/* Left: intro */}
              <div>
                {config.faq_label && (
                  <p className="text-[11px] font-bold tracking-widest text-slate-400 uppercase mb-4">
                    {config.faq_label}
                  </p>
                )}
                <h3 className="text-2xl font-black text-slate-900 mb-4">
                  {config.faq_heading || "A few questions. Clear answers."}
                </h3>
                {config.faq_description && (
                  <p className="text-slate-500 text-sm leading-relaxed mb-8">
                    {config.faq_description}
                  </p>
                )}
                {/* Support box */}
                <div className="border border-slate-200 rounded-2xl p-5">
                  <div className="w-9 h-9 bg-slate-100 rounded-xl flex items-center justify-center mb-3">
                    <HelpCircle className="w-5 h-5 text-slate-500" />
                  </div>
                  <p className="font-bold text-slate-900 text-sm mb-1">Still need a hand?</p>
                  {config.faq_description && (
                    <p className="text-xs text-slate-400 mb-4">Our team usually responds within 1 business day.</p>
                  )}
                  {config.support_button_text && (
                    <a
                      href={config.support_button_url || "/contact"}
                      className="w-full block text-center bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold py-2.5 rounded-xl mb-3 transition-colors"
                    >
                      {config.support_button_text}
                    </a>
                  )}
                  {config.support_email && (
                    <p className="text-xs text-slate-400 text-center">{config.support_email}</p>
                  )}
                </div>
              </div>

              {/* Right: FAQ accordion */}
              <div className="lg:col-span-2 divide-y divide-slate-100">
                {faqItems.map((item, idx) => (
                  <div key={item.id || idx} className="py-4">
                    <button
                      onClick={() => setOpenFaq(openFaq === item.id ? null : item.id)}
                      className="w-full flex items-start gap-4 text-left group"
                    >
                      <span className="text-slate-300 text-sm font-mono flex-shrink-0 w-6">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <span className="flex-1 text-slate-800 font-semibold text-sm group-hover:text-orange-600 transition-colors">
                        {item.question}
                      </span>
                      {openFaq === item.id
                        ? <Minus className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                        : <Plus className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                      }
                    </button>
                    {openFaq === item.id && (
                      <p className="text-slate-500 text-sm leading-relaxed mt-3 ml-10">
                        {item.answer}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default function HomePage() {
  const [config, setConfig] = useState(null);
  const storeSettings = useStoreSettings();
  const [allProducts, setAllProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const updateCart = useCallback(() => setCartCount(getCartCount()), []);
  const updateWishlist = useCallback(() => {
    try { setWishlistCount(JSON.parse(localStorage.getItem("it_wishlist") || "[]").length); } catch { setWishlistCount(0); }
  }, []);

  useEffect(() => {
    updateCart();
    updateWishlist();
    window.addEventListener("cart-updated", updateCart);
    window.addEventListener("wishlist-updated", updateWishlist);
    return () => {
      window.removeEventListener("cart-updated", updateCart);
      window.removeEventListener("wishlist-updated", updateWishlist);
    };
  }, [updateCart, updateWishlist]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [hpRes, prodsRes, colsRes] = await Promise.all([
          getHomepageWithRetry().catch(() => null),
          axios.get(`${API}/products/public?limit=100`).catch(() => ({ data: [] })),
          axios.get(`${API}/collections/public`).catch(() => ({ data: [] })),
        ]);
        if (hpRes && hpRes.data && Object.keys(hpRes.data).length) setConfig(hpRes.data);
        else setLoadError(true);
        setAllProducts(prodsRes.data);
        setCollections(colsRes.data);
      } catch (e) {
        console.error("Failed to load homepage data", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-slate-500 font-medium">Loading…</p>
        </div>
      </div>
    );
  }

  if (!config) {
    if (!loadError) return null;
    return (
      <div className="min-h-screen flex items-center justify-center bg-white" data-testid="homepage-load-error">
        <div className="text-center">
          <p className="text-sm text-slate-600 font-medium mb-3">We couldn't load the store right now.</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold px-5 py-2 rounded-lg transition-colors"
          >Try again</button>
        </div>
      </div>
    );
  }

  const order = config.sections_order || ["hero", "category_grid", "brands", "promo_banners", "category_sections", "categories_showcase", "b2b_section", "recommended", "trust_badges", "newsletter"];
  const menu = config.main_menu || [];

  const renderSection = (key) => {
    switch (key) {
      case "hero":
        return <HeroSection key="hero" config={config.hero} />;
      case "category_grid":
        return <CategoryGridSection key="category_grid" config={config.category_grid} allProducts={allProducts} />;
      case "brands":
        return <BrandsSection key="brands" config={config.brands} />;
      case "promo_banners":
        return <PromoBannersSection key="promo_banners" config={config.promo_banners} />;
      case "category_sections":
        if (!config.category_sections?.enabled) return null;
        return (config.category_sections?.items || []).map((sec) => (
          <CategoryProductSection key={sec.id || sec.title} section={sec} allProducts={allProducts} />
        ));
      case "recommended":
        return <RecommendedSection key="recommended" config={config.recommended} allProducts={allProducts} />;
      case "categories_showcase":
        return <CategoriesShowcaseSection key="categories_showcase" config={config.categories_showcase} />;
      case "b2b_section":
        return <B2BSection key="b2b_section" config={config.b2b_section} />;
      case "trust_badges":
        return <TrustBadgesSection key="trust_badges" config={config.trust_badges} />;
      case "newsletter":
        return <NewsletterSection key="newsletter" config={config.newsletter} />;
      // ── New-design sections ──
      case "category_icons_row":  return <CategoryIconsRow key={key} config={config?.category_icons_row} />;
      case "secondary_slider":    return <SecondarySliderSection key={key} config={config?.secondary_slider} />;
      case "shop_by_price":       return <ShopByPriceSection key={key} products={allProducts} config={config?.shop_by_price} />;
      case "flash_deals":         return <FlashDealsSection key={key} products={allProducts} config={config?.flash_deals} />;
      case "promo_banners_new":   return <PromoBannersNew key={key} products={allProducts} config={config?.promo_banners_new} />;
      case "new_arrivals":        return <NewArrivalsSection key={key} products={allProducts} config={config?.new_arrivals} />;
      case "featured_categories": return <FeaturedCategoriesSection key={key} collections={collections} products={allProducts} config={config?.featured_categories} />;
      case "why_shop":            return <WhyShopSection key={key} config={config?.why_shop} />;
      case "top_deals_week":      return <TopDealsWeekSection key={key} products={allProducts} config={config?.top_deals_week} />;
      case "reseller_app":        return <ResellerAppSection key={key} config={config?.reseller_app} />;
      case "customer_reviews":    return <CustomerReviewsSection key={key} products={allProducts} config={config?.customer_reviews} />;
      default:
        return null;
    }
  };

  // Wrap every section with the admin's enable/disable + device (mobile/desktop/both) settings.
  const renderSectionOuter = (key) => {
    let cfg;
    let node;
    if (key.startsWith("custom:")) {
      cfg = (config.custom_sections || []).find(sec => `custom:${sec.id}` === key);
      if (!cfg) return null;
      node = cfg.type === "category_cards"
        ? <CategoryCardsSection key={key} config={cfg} products={allProducts} collections={collections} />
        : <TileShowcaseSection key={key} config={cfg} />;
    } else {
      cfg = config[key];
      node = renderSection(key);
    }
    if (cfg && cfg.enabled === false) return null;
    const cls = deviceClass(cfg?.device);
    return cls ? <div key={key} className={cls}>{node}</div> : node;
  };

  // Use unified order if admin has configured it; otherwise fall back to legacy + new
  const DEFAULT_NEW_ORDER = ["category_icons_row", "secondary_slider", "shop_by_price", "flash_deals", "promo_banners_new", "new_arrivals", "featured_categories", "why_shop", "top_deals_week", "reseller_app", "customer_reviews"];
  const unifiedOrder = config?.unified_sections_order || [...order, ...DEFAULT_NEW_ORDER];

  return (
    <div className="min-h-screen bg-white">
      <TopBar config={config.top_bar} />
      <SiteHeader
        cartCount={cartCount}
        wishlistCount={wishlistCount}
        cartTotal={0}
        collections={collections}
        menu={menu}
        showNav={config?.nav_bar?.enabled !== false}
      />

      <main>
        {/* All sections rendered in admin-defined unified order */}
        {unifiedOrder.map(key => renderSectionOuter(key))}
      </main>

      <SiteFooter configProp={config.footer} />
    </div>
  );
}
